export interface BlogTeaserOptions {
  title: string;
  intro: string;
  imageUrl?: string;
  buttonText: string;
  buttonUrl: string;
  accentColor?: string;
}

const EMAIL_IMAGE_WIDTH = 800;
const EMAIL_IMAGE_QUALITY = 75;

// Featured images zijn vaak >500KB; mail-clients die remote content via een
// privacy-proxy laden (o.a. Apple Mail) haken daarop af. De Supabase
// render-endpoint levert een geschaalde én cachebare variant
// (cache-control: max-age i.p.v. no-cache op de object-endpoint).
export function toEmailImageUrl(url: string): string {
  const m = url.match(/^(https:\/\/[^/]+\.supabase\.co)\/storage\/v1\/object\/public\/(.+)$/);
  if (!m) return url;
  const [, host, path] = m;
  return `${host}/storage/v1/render/image/public/${path}?width=${EMAIL_IMAGE_WIDTH}&quality=${EMAIL_IMAGE_QUALITY}`;
}

// Image transformations zijn een per-project instelling (staging heeft ze
// bijv. niet aanstaan → 403). Daarom runtime verifiëren en terugvallen op
// het originele URL als de render-endpoint niet werkt.
export async function resolveEmailImageUrl(url: string | null | undefined): Promise<string | undefined> {
  if (!url) return undefined;
  const candidate = toEmailImageUrl(url);
  if (candidate === url) return url;
  try {
    const res = await fetch(candidate, { method: 'HEAD' });
    return res.ok ? candidate : url;
  } catch {
    return url;
  }
}

export function generateBlogTeaserHtml(options: BlogTeaserOptions): string {
  const {
    title,
    intro,
    imageUrl,
    buttonText,
    buttonUrl,
    accentColor = '#FF6B35',
  } = options;

  const imageHtml = imageUrl
    ? `<img src="${imageUrl}" alt="${title}" style="width: 100%; height: auto; border-radius: 0.5rem; margin-bottom: 1rem;" />`
    : '';

  return `
    <div data-blog-teaser-wrapper="true" style="border: 1px solid #e5e7eb; border-radius: 0.5rem; padding: 1rem; background-color: #f9fafb; margin: 1.5rem 0;">
      ${imageHtml}
      <h3 style="font-size: 1.25rem; font-weight: 600; color: #1f2937; margin-bottom: 0.5rem;">${title}</h3>
      <p style="color: #4b5563; margin-bottom: 1rem;">${intro}</p>
      <p style="text-align: left; margin: 0;">
        <a href="${buttonUrl}" data-teaser-button="true" style="display: inline-block; background-color: ${accentColor}; color: white; padding: 0.5rem 1rem; border-radius: 0.375rem; text-decoration: none; font-weight: 500;">${buttonText}</a>
      </p>
    </div>
  `.trim();
}

export function extractBlogTeaserData(element: HTMLElement): BlogTeaserOptions | null {
  const img = element.querySelector('img');
  const h3 = element.querySelector('h3');
  const p = element.querySelector('p:not([style*="text-align"])');
  const button = element.querySelector('a[data-teaser-button]');

  if (!h3 || !button) return null;

  return {
    title: h3.textContent?.trim() || '',
    intro: p?.textContent?.trim() || '',
    imageUrl: img?.getAttribute('src') || undefined,
    buttonText: button.textContent?.trim() || 'Lees meer',
    buttonUrl: button.getAttribute('href') || '#',
    accentColor: extractColorFromStyle(button.getAttribute('style') || ''),
  };
}

function extractColorFromStyle(style: string): string {
  const match = style.match(/background-color:\s*([^;]+)/);
  return match ? match[1].trim() : '#FF6B35';
}
